import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import swal from "sweetalert";
import Footer from "./common/Footer";
import Sidebar from "./common/Sidebar";
import logo from "../assets/img/logo/newJkLogo.png";
import { submitRegistrationForm } from "./api/registrationFormApi";
import * as Constants from "./Constants/index";

export default function RegistrationForm() {
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastLookupPhone = useRef("");

  const lookupEndpoint = `${Constants.baseUrl}distributor?mobile={phoneNumber}`;

  // ✅ NEW dynamic update API
  const updateEndpoint = `${Constants.baseUrl}distributor/update`;

  const formik = useFormik({
    initialValues: {
      partyCode: "",
      partyName: "",
      cluster: "",
      district: "",
      state: "",
      contactName: "",
      phoneNumber: "",
    },
    validate: (values) => {
      const errors = {};

      // if (!values.partyCode.trim()) errors.partyCode = "Party code is required.";
      // if (!values.partyName.trim()) errors.partyName = "Party name is required.";
      // if (!values.cluster.trim()) errors.cluster = "Cluster is required.";
      // if (!values.district.trim()) errors.district = "District is required.";
      // if (!values.state.trim()) errors.state = "State is required.";
      // if (!values.contactName.trim()) errors.contactName = "Name is required.";

      if (!values.phoneNumber.trim()) {
        errors.phoneNumber = "Phone number is required.";
      } else if (!/^\d{10}$/.test(values.phoneNumber)) {
        errors.phoneNumber = "Phone number must be 10 digits.";
      }

      return errors;
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);

        // ✅ Existing API
        await submitRegistrationForm(values);

        // ✅ NEW UPDATE API (dynamic base_url)
        await axios.post(
          updateEndpoint,
          {
            id: 0,
            mobile: values.phoneNumber,
            name: values.contactName,
            state: values.state,
            district: values.district,
            cluster: values.cluster,
            firmName: values.partyName,
            firmCode: values.partyCode,
            status: "ACTIVE",
          },
          {
            headers: {
              appVersion: Constants.av,
              platform: Constants.pt,
              company: Constants.company,
              program: Constants.program,
              "Content-Type": "application/json",
            },
          }
        );

        swal({
          title: "Thank You",
          text: "Thank you for participating",
          icon: "success",
          timer: 2500,
          buttons: false,
        });

        resetForm();
        lastLookupPhone.current = "";

      } catch (error) {
        swal({
          title: "Error",
          text: "Unable to submit registration form.",
          icon: "error",
          timer: 2000,
          buttons: false,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const clearAutoFilledFields = () => {
    formik.setFieldValue("contactName", "", false);
    formik.setFieldValue("partyCode", "", false);
    formik.setFieldValue("partyName", "", false);
    formik.setFieldValue("cluster", "", false);
    formik.setFieldValue("district", "", false);
    formik.setFieldValue("state", "", false);
  };

  const lookupRegistrationByPhone = async (phoneNumber) => {
    const endpoint = lookupEndpoint.replace("{phoneNumber}", phoneNumber);

    const response = await axios.get(endpoint, {
      headers: {
        appVersion: Constants.av,
        platform: Constants.pt,
        company: Constants.company,
        program: Constants.program,
      },
    });

    if (response?.data?.code !== 10001 || !response?.data?.output) {
      return {
        success: true,
        found: false,
        endpoint,
        data: null,
        message: response?.data?.msg || "",
      };
    }

    return {
      success: true,
      found: true,
      endpoint,
      message: response.data.msg || "Distributor found",
      data: {
        phoneNumber: response.data.output.mobile || phoneNumber,
        contactName: response.data.output.name || "",
        partyCode: response.data.output.firmCode || "",
        partyName: response.data.output.firmName || "",
        cluster: response.data.output.cluster || "",
        district: response.data.output.district || "",
        state: response.data.output.state || "",
      },
    };
  };

  useEffect(() => {
    const phoneNumber = formik.values.phoneNumber;

    if (phoneNumber.length !== 10) {
      if (lastLookupPhone.current) {
        clearAutoFilledFields();
        lastLookupPhone.current = "";
      }
      setIsLookupLoading(false);
      return;
    }

    if (lastLookupPhone.current === phoneNumber) return;

    let isActive = true;

    const fetchRegistrationData = async () => {
      try {
        setIsLookupLoading(true);
        const response = await lookupRegistrationByPhone(phoneNumber);

        if (!isActive) return;

        lastLookupPhone.current = phoneNumber;

        if (response.found && response.data) {
          formik.setFieldValue("contactName", response.data.contactName || "", false);
          formik.setFieldValue("partyCode", response.data.partyCode || "", false);
          formik.setFieldValue("partyName", response.data.partyName || "", false);
          formik.setFieldValue("cluster", response.data.cluster || "", false);
          formik.setFieldValue("district", response.data.district || "", false);
          formik.setFieldValue("state", response.data.state || "", false);
        } else {
          clearAutoFilledFields();
        }
      } catch (error) {
        if (isActive) {
          clearAutoFilledFields();
          swal({
            text: error?.response?.data?.msg || error?.message || "Failed",
            icon: "error",
            timer: 2000,
            buttons: false,
          });
        }
      } finally {
        if (isActive) setIsLookupLoading(false);
      }
    };

    fetchRegistrationData();

    return () => {
      isActive = false;
    };
  }, [formik.values.phoneNumber]);

  const fieldStyle = {
    width: "100%",
    minHeight: "44px",
    border: "1px solid #d7e1d7",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fff",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "15px",
    fontWeight: 800,
    color: "#295c31",
  };

  const errorStyle = {
    color: "#c0392b",
    fontSize: "12px",
    marginTop: "6px",
  };

  const renderInput = (name, label, placeholder, maxLength) => (
    <div className="col-12 col-md-6" style={{ marginBottom: "18px" }}>
      <label style={labelStyle}>{label}</label>
      <input
        type="text"
        name={name}
        placeholder={placeholder}
        value={formik.values[name]}
        onChange={(e) => {
          if (name === "phoneNumber") {
            const value = e.target.value.replace(/\D/g, "").slice(0, 10);
            formik.setFieldValue(name, value);
            return;
          }
          if (maxLength) {
            formik.setFieldValue(name, e.target.value.slice(0, maxLength));
            return;
          }
          formik.handleChange(e);
        }}
        onBlur={formik.handleBlur}
        style={fieldStyle}
      />
      {formik.touched[name] && formik.errors[name] ? (
        <div style={errorStyle}>{formik.errors[name]}</div>
      ) : null}
    </div>
  );

  return (
    <div style={{ display: "flex", width: "100%", minHeight: "100vh" }}>
      <div style={{ flex: 1, padding: "20px" }}>
        <div style={{ backgroundColor: "#fff", padding: "20px" }}>
          <img src={logo} alt="logo" style={{ height: "60px", marginBottom: "15px", marginLeft: "10px" }} />

          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              {renderInput("phoneNumber", "Phone Number", "Enter Your Mobile number")}
              {renderInput("contactName", "Name", "Enter name")}
              {renderInput("partyCode", "Party Code", "Enter code")}
              {renderInput("partyName", "Party Name", "Enter name")}
              {renderInput("cluster", "Cluster", "Enter cluster")}
              {renderInput("district", "District", "Enter district")}
              {renderInput("state", "State", "Enter state")}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%",
                minHeight: "44px",
                borderRadius: "10px",
                border: "none",
                appearance: "none",
                WebkitAppearance: "none",
                backgroundColor: isSubmitting ? "#89c996" : "#2eb545",
                color: "#fff",
                fontWeight: 700,
                padding: "10px 18px",
                marginTop: "12px",
                boxShadow: "0 10px 20px rgba(46, 181, 69, 0.2)",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.85 : 1,
                fontSize: "18px",
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
        <Footer />
      </div>
    </div>
  );
}
